// No 'use client' here, deliberately, even though this calls a hook: the directive would make this
// a client-boundary module, and `PasswordInput.zodValidator` is read from App Router modules that
// are server modules by default (apps/client/src/app/constants/*Schema.ts) — dotting into a client
// module from one throws at runtime. Mantine's own PasswordInput carries the directive, so the
// boundary is still there underneath. Same shape as the EmailInput composition this replaced.
import { PasswordInput as MantinePasswordInput } from '@mantine/core';
import type { PasswordInputProps as MantinePasswordInputProps } from '@mantine/core';
import { useTranslation } from 'next-i18next/pages';
import { FiLock } from 'react-icons/fi';
import { z } from 'zod/v3';

import type { LockedProps } from '../types';

type PasswordInputProps = Omit<MantinePasswordInputProps, LockedProps>;

export const PasswordInput = (props: PasswordInputProps) => {
  const { t } = useTranslation();

  return (
    <MantinePasswordInput
      label={t('Password')}
      placeholder={t('Enter your password')}
      leftSection={<FiLock aria-hidden="true" focusable="false" />}
      {...props}
    />
  );
};

/**
 * A zod validator for a password.
 * @usage
 * ```ts
 * const form = useForm({
 *   validate: schemaResolver(
 *     z.object({
 *       password: PasswordInput.zodValidator,
 *     }),
 *     { sync: true },
 *   ),
 * });
 * ```
 */
PasswordInput.zodValidator = z.string().min(1, 'Password is required');
